import { useEffect, useState } from 'react';

import { Accordion as ReachAccordion } from '@reach/accordion';
import { AccordionSection } from './AccordionSection';
import type { AccordionProps } from './AccordionTypes';
import { AccordionContext } from './AccordionContext';

/**
 * Accordion component.
 *
 * {@link https://zeroheight.com/76c439891/p/00059f-accordion Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-accordion--base-accordion Code documentation}
 *
 * @example
 * <Accordion>
 *  <Accordion.Section title="Section title" description="Section description" icon="flame">
 *    <Text>Section content</Text>
 *  </Accordion.Section>
 * </Accordion>
 */
export const Accordion = ({
  onChange,
  onToggleSection,
  sectionToShow = -1,
  setUncontrolled = false,
  isWizard = false,
  size = 'm',
  children,
  className,
  readOnly = false,
}: AccordionProps): JSX.Element => {
  const [openedSection, setOpenedSection] = useState<number>(sectionToShow);

  useEffect(() => {
    if (setUncontrolled) {
      setOpenedSection(sectionToShow);
    }
  }, [sectionToShow, setUncontrolled]);

  const toggleSection = (index: number) => {
    let newIndex = -1;
    if (openedSection !== index) {
      newIndex = index;
    }
    if (onChange) {
      onChange(newIndex);
    }
    if (!setUncontrolled) {
      setOpenedSection(newIndex);
    }
  };
  return (
    <AccordionContext.Provider value={{ size, isWizard, onToggleSection }}>
      <ReachAccordion
        index={openedSection}
        onChange={toggleSection}
        className={className}
        readOnly={readOnly}
      >
        {children}
      </ReachAccordion>
    </AccordionContext.Provider>
  );
};

Accordion.displayName = 'Accordion';

Accordion.Section = AccordionSection;
